import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Edit3, Copy, Play, Check, X, FileText, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { promptsApi } from '../api';
import { Card, Button, Badge, Input, Spinner, EmptyState, PageHeader, ModalOverlay } from '../components/UI';

const PLACEHOLDERS = ['{{context}}', '{{question}}'];

function PromptEditor({ prompt, onSave, onClose }) {
  const { t } = useTranslation(['prompts', 'common']);
  const [form, setForm] = useState({
    name: prompt?.name || '',
    description: prompt?.description || '',
    template: prompt?.template || `Sei un assistente AI. Rispondi basandoti esclusivamente sul contesto fornito.

CONTESTO:
{{context}}

DOMANDA: {{question}}

RISPOSTA:`
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const insertPlaceholder = (ph) => {
    const ta = document.getElementById('prompt-textarea');
    if (!ta) { set('template', form.template + ph); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = form.template.substring(0, start) + ph + form.template.substring(end);
    set('template', newVal);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + ph.length, start + ph.length); }, 0);
  };

  const hasContext = form.template.includes('{{context}}');
  const hasQuestion = form.template.includes('{{question}}');

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)', width: 'min(720px, 100%)',
        maxHeight: 'calc(100dvh - 40px)', display: 'flex', flexDirection: 'column',
        animation: 'fadeIn 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>
              {prompt ? '✏️ ' + t('edit') : '✨ ' + t('new')}
            </h2>
            <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 4 }}>{t('placeholdersHint')}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div className="grid-form-2" style={{ marginBottom: 16 }}>
            <Input label={t('name') + ' *'} value={form.name} onChange={e => set('name', e.target.value)} placeholder={t('namePlaceholder')} />
            <Input label={t('description')} value={form.description} onChange={e => set('description', e.target.value)} placeholder={t('descriptionPlaceholder')} />
          </div>

          {/* Placeholder buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('insertLabel')}</span>
            {PLACEHOLDERS.map(ph => (
              <button key={ph} onClick={() => insertPlaceholder(ph)} style={{
                padding: '4px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)',
                borderRadius: 4, color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 12, cursor: 'pointer'
              }}>{ph}</button>
            ))}
          </div>

          {/* Validation badges */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {hasContext ? <Check size={12} style={{ color: 'var(--green)' }} /> : <X size={12} style={{ color: 'var(--red)' }} />}
              <span style={{ fontSize: 11, color: hasContext ? 'var(--green)' : 'var(--red)' }}>{'{{context}}'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {hasQuestion ? <Check size={12} style={{ color: 'var(--green)' }} /> : <X size={12} style={{ color: 'var(--red)' }} />}
              <span style={{ fontSize: 11, color: hasQuestion ? 'var(--green)' : 'var(--red)' }}>{'{{question}}'}</span>
            </div>
          </div>

          {/* Textarea */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('content')} *</label>
            <textarea
              id="prompt-textarea"
              value={form.template}
              onChange={e => set('template', e.target.value)}
              rows={14}
              style={{
                width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
                fontSize: 13, padding: '12px', outline: 'none', resize: 'vertical', lineHeight: 1.7
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>{t('cancel', { ns: 'common' })}</Button>
          <Button
            onClick={() => {
              if (!form.name) return toast.error(t('nameRequired'));
              if (!form.template) return toast.error(t('templateRequired'));
              onSave(form);
            }}
          >
            {prompt ? t('saveChanges') : t('createPrompt')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default function Prompts() {
  const { t } = useTranslation(['prompts', 'common']);
  const qc = useQueryClient();
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['prompts'], queryFn: promptsApi.list });

  const createMut = useMutation({
    mutationFn: promptsApi.create,
    onSuccess: () => { qc.invalidateQueries(['prompts']); setShowEditor(false); toast.success(t('created')); }
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => promptsApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['prompts']); setShowEditor(false); setEditingPrompt(null); toast.success(t('updated')); }
  });

  const deleteMut = useMutation({
    mutationFn: promptsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['prompts']); toast.success(t('deleted')); }
  });

  const prompts = data?.prompts || [];

  const handleSave = (form) => {
    if (editingPrompt) {
      updateMut.mutate({ id: editingPrompt.id, data: form });
    } else {
      createMut.mutate(form);
    }
  };

  const handleCopy = (prompt) => {
    navigator.clipboard.writeText(prompt.template);
    toast.success(t('copied'));
  };

  return (
    <div className="animate-fade">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={<Button icon={Plus} onClick={() => { setEditingPrompt(null); setShowEditor(true); }}>{t('new')}</Button>}
      />

      <div className="page-content">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
        ) : prompts.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={t('noPrompts')}
            description={t('noPromptsDesc')}
            action={<Button icon={Plus} onClick={() => setShowEditor(true)}>{t('createPrompt')}</Button>}
          />
        ) : (
          <div className="grid-cards">
            {prompts.map(prompt => (
              <Card key={prompt.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-1)' }}>
                      {prompt.name}
                    </div>
                    <div
                      title={t('copyIdTooltip', { ns: 'common' })}
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(prompt.id); toast.success(t('idCopied', { ns: 'common' })); }}
                      style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', cursor: 'pointer', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <span>ID: {prompt.id}</span>
                      <span style={{ opacity: 0.5 }}>⧉</span>
                    </div>
                    {prompt.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{prompt.description}</div>
                    )}
                  </div>
                  {prompt.isDefault && <Badge color="cyan">DEFAULT</Badge>}
                </div>

                {/* Preview of template */}
                <div style={{
                  background: 'var(--bg-3)', borderRadius: 6, padding: '10px 12px',
                  marginBottom: 14, fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: 'var(--text-3)', lineHeight: 1.6,
                  maxHeight: previewing === prompt.id ? 'none' : 80,
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer'
                }} onClick={() => setPreviewing(previewing === prompt.id ? null : prompt.id)}>
                  {prompt.template.split('\n').map((line, i) => {
                    const highlighted = line.replace(/\{\{(\w+)\}\}/g, (_, v) =>
                      `<span style="color:var(--accent);font-weight:600">{{${v}}}</span>`);
                    return <div key={i} dangerouslySetInnerHTML={{ __html: highlighted || '&nbsp;' }} />;
                  })}
                  {previewing !== prompt.id && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 30, background: 'linear-gradient(transparent, var(--bg-3))' }} />
                  )}
                </div>

                {/* Validation */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {['{{context}}', '{{question}}'].map(ph => {
                    const has = prompt.template.includes(ph);
                    return (
                      <div key={ph} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {has ? <Check size={11} style={{ color: 'var(--green)' }} /> : <X size={11} style={{ color: 'var(--red)' }} />}
                        <span style={{ fontSize: 10, color: has ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--font-mono)' }}>{ph}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" icon={Edit3} style={{ flex: 1 }}
                    onClick={() => { setEditingPrompt(prompt); setShowEditor(true); }}>
                    {t('edit')}
                  </Button>
                  <Button variant="ghost" size="sm" icon={Copy} onClick={() => handleCopy(prompt)} />
                  {!prompt.isDefault && (
                    <Button variant="danger" size="sm" icon={Trash2} onClick={() => {
                      if (window.confirm(t('deleteConfirm', { name: prompt.name }))) deleteMut.mutate(prompt.id);
                    }} />
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showEditor && (
        <PromptEditor
          prompt={editingPrompt}
          onClose={() => { setShowEditor(false); setEditingPrompt(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
